import { trigger, transition, style, animate } from '@angular/animations';
import { Component, Input, OnDestroy, OnInit, QueryList, ViewChildren, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SharedService } from '@shared/services/shared.service';
import { IEntityOwner } from 'app/models/licenses/entity-owner';
import { EntityOwnerService } from 'app/services/license/in-home-care/entity-owner.service';
import { AddressComponent } from '../address/address.component';
import { DocumentComponent } from '../document/document.component';
import { IAddress } from 'app/models/address/address';
import { IStatus } from 'app/models/licenses/status';
import { ILicenseChangeDialog } from 'app/components/in-home-care/in-home-care-change/in-home-care-change-dialog.component';

@Component({
  selector: 'entity-owner',
  templateUrl: './entity-owner.component.html',
  styleUrls: ['./entity-owner.component.scss'],
  animations: [
    trigger('slideRight', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
      ])
    ])
  ]
})

export class EntityOwnerComponent implements OnInit, OnDestroy {
  @ViewChildren(DocumentComponent) documents!: QueryList<DocumentComponent>;
  @ViewChildren(AddressComponent) addresses!: QueryList<AddressComponent>;
  @Input() entityId: number = 0;
  @Input() entityGuid: string = '';
  @Input() entityType: string = '';
  @Input() parentId: number = 0;
  @Input() isSubunit: boolean = false;
  @Input() canChange: boolean = true;
  @Input() isChange: boolean = false;
  @Input() isEditable: boolean = false;
  @Input() statuses: IStatus[] = [];
  @Input() showBackgroundCheck: boolean = true;

  public managingOwner: boolean = false;
  public isOutOfState: boolean = false;
  public subunitHasParentOwner: boolean = true;
  public newOwnerCreated: boolean = false;
  public includeArchived: boolean = false;
  public entityOwnerTypes: string[] = ['Background Check Request']
  public owner: IEntityOwner = {
    id: 0,
    parentId: 0,
    firstName: '',
    lastName: '',
    phone: '',
    fax: '',
    taxId: '',
    contactWithClient: null,
    backgroundCheckCompleted: false,
    backgroundCheckDate: '',
    archived: false
  }
  public owners: IEntityOwner[] = [];
  public parentOwners: IEntityOwner[] = [];
  public ownerForm = new FormGroup({
    firstName: new FormControl('', Validators.required),
    phone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    fax: new FormControl('', [Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    taxId: new FormControl('', Validators.required),
    contactWithClient: new FormControl<boolean | null>(null),
    backgroundCheckCompleted: new FormControl(false),
    backgroundCheckDate: new FormControl<Date | null>(null)
  });
  public ownerBackgroundCheckDates: { [key: number]: Date } = {};
  public sharedService = inject(SharedService);
  public ownerService = inject(EntityOwnerService);

  ngOnInit(): void {
    this.getOwners(this.includeArchived);
  }

  ngOnDestroy(): void {
    if(this.managingOwner){
      this.saveOwner();
    }
  }

  getOwners(includeArchived: boolean): void {
    this.includeArchived = includeArchived;
    let lookupId = this.entityId;
    if(this.isSubunit){
      if(this.parentId === 0) {
        this.sharedService.openNotice('Add a parent agency to view parent owners.');
        return;
      }
      lookupId = this.parentId;
    }

    this.ownerService.getEntityOwners(lookupId, this.entityType, this.includeArchived).subscribe({
      next: response => this.owners = response,
      error: e => console.log('get entity owners error', e),
      complete: () => {
        if(this.owners.length === 0 && this.isChange){
          this.createOwner();
          this.entityOwnerTypes.push('Owner Agreement');
        }
        else{
          this.owners.forEach(owner => {
            if(owner.backgroundCheckDate){
              this.ownerBackgroundCheckDates[owner.id] = new Date(owner.backgroundCheckDate);
            }
          });
        }
      }
    });
  }

  selectOwner(owner: IEntityOwner): void {
    this.owner = owner;
    this.managingOwner = true;
    this.updateOwnerForm(owner);
  }

  toggleOwnerForm(adding: boolean): void{
    this.managingOwner = adding;
    this.updateOwnerForm(this.getEmptyOwner());
    if(!adding){
      if(this.newOwnerCreated){
        this.deleteOwner(this.owner.id);
      }
      else{
        this.owner = this.getEmptyOwner();
      }
    }
  }

  getEmptyOwner(): IEntityOwner {
    return {
      id: 0,
      parentId: 0,
      firstName: '',
      lastName: '',
      phone: '',
      fax: '',
      taxId: '',
      contactWithClient: null,
      backgroundCheckCompleted: false,
      backgroundCheckDate: '',
      archived: false
    }
  }

  updateOwnerForm(owner: IEntityOwner): void {
    this.ownerForm.reset({
      firstName: owner.firstName,
      phone: owner.phone,
      fax: owner.fax,
      taxId: owner.taxId,
      contactWithClient: owner.contactWithClient,
      backgroundCheckCompleted: owner.backgroundCheckCompleted
    });

    if(owner.backgroundCheckDate){
      this.ownerForm.controls['backgroundCheckDate'].setValue(new Date(owner.backgroundCheckDate));
    }
  }

  updateOwner(): void {
    let form = this.ownerForm.getRawValue();
    this.owner.firstName = form.firstName ?? '';
    this.owner.phone = form.phone ?? '';
    this.owner.fax = form.fax ?? '';
    this.owner.taxId = form.taxId ?? '';
    this.owner.contactWithClient = form.contactWithClient;
    this.owner.backgroundCheckCompleted = form.backgroundCheckCompleted ?? false;
    this.owner.backgroundCheckDate = form.backgroundCheckDate?.toDateString() ?? '';
  }

  createOwner(): void {
    this.owner.parentId = this.entityId;
    this.ownerService.createEntityOwner(this.owner, this.entityType).subscribe({
      next: response => {
        this.owners.push(response);
        this.owner = response;
      },
      error: e => {
        this.sharedService.toastr.error("Error Creating Entity Owner");
        console.log('error creating entity owner', e);
      },
      complete: () => {
        this.toggleOwnerForm(true);
        this.newOwnerCreated = true;
      }
    });
  }

  updateOwnerBackgroundCheck(owner: IEntityOwner): void {
    if(this.ownerBackgroundCheckDates[owner.id]){
      owner.backgroundCheckDate = this.ownerBackgroundCheckDates[owner.id].toDateString();
    }
    this.ownerService.updateEntityOwner(owner).subscribe({
      complete: () => {
        this.sharedService.toastr.success("Background Check Updated");
      },
      error: e => {
        this.sharedService.toastr.error("Could Not Update Background Check");
        console.log('error updating entity owner', e);
      }
    });
  }

  saveOwner(): void {
    this.updateOwner();
    this.ownerService.updateEntityOwner(this.owner).subscribe({
      complete: () => {
        this.newOwnerCreated = false;
        this.toggleOwnerForm(false);
      },
      error: e => {
        this.sharedService.toastr.error("Could Not Save Owner");
        console.log('error updating entity owner', e);
      }
    });
  }

  archiveOwner(owner: IEntityOwner, archive: boolean): void {
    let archiving: string = archive? 'Archive ' : 'Un-Archive ';
    this.sharedService.openConfirm(archiving + 'this entity owner?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.ownerService.archiveEntityOwner(owner.id, archive).subscribe({
            complete: () => {
              owner.archived = archive;
              if(archive){
                this.owners = this.owners.filter(o => o.id !== owner.id);
              }
              this.toggleOwnerForm(false);
            },
            error: e => {
              this.sharedService.toastr.error("Could Not Archive Owner");
              console.log('error archiving entity owner', e);
            }
          });
        }
      }
    );
  }

  confirmDelete(ownerId: number): void {
    this.sharedService.openConfirm('Delete entity owner ' + this.owner.firstName  + ' ?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.deleteOwner(ownerId);
        }
      }
    );
  }

  deleteOwner(ownerId: number): void {
    this.ownerService.deleteEntityOwner(ownerId).subscribe({
      complete: () => {
        this.newOwnerCreated = false;
        this.owners = this.owners.filter(a => a.id !== ownerId);
        this.owner= this.getEmptyOwner();
      },
      error: e => console.log('error deleting owner', e)
    });
  }

  requestChange(entityId: number, type: string){
    let ownerToDelete: IEntityOwner = this.getEmptyOwner();
    if(type === this.sharedService.delete){
      if(this.owners.length === 1){
        this.sharedService.openNotice(`The agency must have at least one owner.
          If you want to change the owner select the option to change the owner instead.`);
        return;
      }
      ownerToDelete = this.owners.find(o => o.id === entityId) ?? this.getEmptyOwner();
    }

    let dialogData: ILicenseChangeDialog = {
      licenseType: this.sharedService.inHomeCareLicense,
      licenseId: this.entityId,
      licenseGuid: this.entityGuid,
      entityId: entityId,
      entityType: this.sharedService.entityOwner,
      originalValue: type === this.sharedService.delete? ownerToDelete : '',
      newValue: '',
      changeAction: type
    }

    this.sharedService.requestChange(dialogData);
  }

  updateOutOfState(isOutOfState: boolean): void {
    this.isOutOfState = isOutOfState;
  }

  downloadReferenceDocument(documentType: string): void {
    this.documents.toArray()[0].getReferenceDocument(documentType);
  }

  validateAddresses(): string[] {
    let errors: string[] = [];
    this.addresses.toArray().forEach(address => {
      let addressSet: IAddress[] = address.addresses;
      if(!address.getPhysicalAddress(addressSet)){
        let owner = this.owners.find(o => o.id === address.entityId) ?? this.getEmptyOwner();
        errors.push('Owner ' + owner.firstName + ' must have a physical address');
      }
    });
    return errors;
  }

  validateDocuments(): string[] {
    let errors: string[] = [];
    this.documents.toArray().forEach(document => {
      let documentSet = document.documents;
      let owner = this.owners.find(o => o.id === document.entityId) ?? this.getEmptyOwner();
      if(owner.contactWithClient && !documentSet.some(d => d.types.some(t => t.type === 'Background Check Request'))){
        errors.push('Owner ' + owner.firstName + ' must have a background check request');
      }
      if(this.isChange && !documentSet.some(d => d.types.some(t => t.type === 'Owner Agreement'))){
        errors.push('Owner ' + owner.firstName + ' must have an owner agreement');
      }
    });
    return errors;
  }

  public get locked(): boolean {
    return !this.statuses.some(s => s.label === this.sharedService.started) && !this.isEditable;
  }

  public get approved(): boolean {
    return this.statuses.some(s => s.label === this.sharedService.approved);
  }
}
